import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Alert, Button, Form, Badge, Spinner } from 'react-bootstrap';
import SEO from '../components/common/SEO';
import SkeletonLoader from '../components/common/SkeletonLoader';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import FullPageChatRoom from '../components/chat/FullPageChatRoom';
import './ChatPage.scss';

interface ChatRoom {
  id: number;
  name: string;
  content_object: string;
  content_type: string;
  object_id: number;
  last_message_time: string;
  last_message_time_ago: string;
  unread_count: number;
  has_unread: boolean;
  participant_count: number;
  is_public: boolean;
  created_at: string;
  last_message?: {
    content: string;
    message_type: string;
    sender_name: string;
    created_at: string;
  };
}

interface Message {
  id: number;
  content: string;
  message_type: 'text' | 'image' | 'file';
  is_system_message: boolean;
  sender?: {
    id: number;
    username: string;
    full_name: string;
  };
  is_outgoing: boolean;
  created_at: string;
  time_ago: string;
  file_url?: string;
  is_read: boolean;
}

const ChatPage: React.FC = () => {
  const { roomId } = useParams<{ roomId?: string }>();
  const navigate = useNavigate();
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [activeChatRoom, setActiveChatRoom] = useState<ChatRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchChatRooms = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await apiService.chat.getChatRooms();
      
      if (response.success) {
        setChatRooms(response.chat_rooms || []);
      } else {
        setError('Failed to load chat rooms');
      }
    } catch (err: any) {
      // Handle specific error cases
      const status = err.response?.status;
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      
      if (status === 403) {
        // 403 Forbidden - User doesn't have permission to access chat
        setError('You do not have permission to access chat. Please contact support if you believe this is an error.');
      } else if (status === 401) {
        // 401 Unauthorized - User not authenticated
        setError('Please log in to access chat.');
      } else if (status === 404) {
        // 404 Not Found - Chat feature might not be available
        setError('Chat feature is not available at this time.');
      } else {
        // Other errors
        setError(errorMessage || 'Failed to load chat rooms. Please try again later.');
      }
      
      // Set empty array on error to show empty state
      setChatRooms([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleChatRoomSelect = (chatRoom: ChatRoom) => {
    setActiveChatRoom(chatRoom);
    setShowMobileChat(true);
    navigate(`/chat/${chatRoom.id}`);
  };

  const handleBackToList = () => {
    setActiveChatRoom(null);
    setShowMobileChat(false);
    navigate('/chat');
  };

  const filteredChatRooms = chatRooms.filter(room =>
    room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.content_object.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnreadCount = chatRooms.reduce((sum, room) => sum + room.unread_count, 0);

  // Initial fetch on mount only
  useEffect(() => {
    fetchChatRooms();
  }, []);

  // Handle URL parameters for direct room access
  useEffect(() => {
    if (roomId && chatRooms.length > 0) {
      const room = chatRooms.find(r => r.id === parseInt(roomId));
      if (room) {
        setActiveChatRoom(room);
        setShowMobileChat(true);
      } else {
        // Room not found, redirect to chat list
        navigate('/chat');
      }
    }
  }, [roomId, chatRooms, navigate]);

  // Auto-refresh when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      fetchChatRooms(true);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  if (loading) {
    return (
      <div className="chat-page">
        <Container fluid className="chat-page-container">
          <Row className="chat-page-row g-0">
            <Col md={4} lg={3} className="chat-sidebar">
              <div className="chat-sidebar-header p-3">
                <SkeletonLoader type="text" width="150px" height="28px" className="mb-3" />
                <SkeletonLoader type="rect" width="100%" height="40px" className="mb-3" />
              </div>
              <div className="chat-room-list">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="chat-room-item-skeleton p-3 border-bottom">
                    <div className="d-flex align-items-center gap-3">
                      <SkeletonLoader type="circle" width="48px" height="48px" />
                      <div style={{ flex: 1 }}>
                        <SkeletonLoader type="text" width="70%" height="18px" className="mb-2" />
                        <SkeletonLoader type="text" width="50%" height="14px" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
            <Col md={8} lg={9} className="chat-main">
              <div className="chat-main-placeholder d-flex align-items-center justify-content-center h-100">
                <div className="text-center">
                  <SkeletonLoader type="circle" width="80px" height="80px" className="mb-3 mx-auto" />
                  <SkeletonLoader type="text" width="200px" height="20px" className="mx-auto" />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Determine page title based on active chat room
  const pageTitle = activeChatRoom 
    ? `${activeChatRoom.name} | Chat | Quicket`
    : 'Chat | Quicket';
  const pageDescription = activeChatRoom
    ? `Chat with ${activeChatRoom.name} on Quicket`
    : 'Connect and chat with event organizers and attendees on Quicket';

  return (
    <div className="chat-page">
      <SEO 
        title={pageTitle}
        description={pageDescription}
      />
      <Container fluid className="chat-page-container">
        <Row className="chat-page-row g-0">
          {/* Chat List Sidebar */}
          <Col 
            md={4} 
            lg={3} 
            className={`chat-sidebar ${!showMobileChat ? 'show' : 'd-none d-md-block'}`}
          >
            <div className="chat-sidebar-header">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                  <i className="fas fa-comments me-2 text-primary"></i>
                  Messages
                  {totalUnreadCount > 0 && (
                    <Badge bg="primary" className="ms-2 unread-badge">
                      {totalUnreadCount}
                    </Badge>
                  )}
                </h4>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => fetchChatRooms(true)}
                  disabled={refreshing}
                  className="refresh-button"
                  title="Refresh"
                >
                  {refreshing ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fas fa-sync-alt"></i>
                  )}
                </Button>
              </div>

              {/* Search */}
              <Form.Group className="mb-3">
                <div className="search-input-wrapper">
                  <i className="fas fa-search search-icon"></i>
                  <Form.Control
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </Form.Group>
            </div>

            <div className="chat-list">
              {error && (
                <Alert variant="warning" className="m-3">
                  <Alert.Heading>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Unable to Load Chat
                  </Alert.Heading>
                  <p className="mb-2">{error}</p>
                  {error.includes('log in') && (
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => navigate('/login')}
                    >
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Go to Login
                    </Button>
                  )}
                </Alert>
              )}

              {!error && filteredChatRooms.length > 0 ? (
                filteredChatRooms.map((room) => (
                  <div
                    key={room.id}
                    className={`chat-item ${room.has_unread ? 'unread' : ''} ${activeChatRoom?.id === room.id ? 'active' : ''}`}
                    onClick={() => handleChatRoomSelect(room)}
                  >
                    <div className="chat-item-content">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h6 className="mb-0 chat-item-title">{room.name}</h6>
                        <div className="chat-item-meta">
                          <small className="text-muted">
                            {room.last_message_time_ago}
                          </small>
                          {room.unread_count > 0 && (
                            <Badge bg="primary" className="ms-2">
                              {room.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <p className="mb-1 text-muted small chat-item-context">
                        {room.content_object}
                      </p>
                      
                      {room.last_message && (
                        <p className="mb-0 text-truncate small chat-item-preview">
                          <strong>{room.last_message.sender_name}:</strong>{' '}
                          {room.last_message.message_type === 'text' 
                            ? room.last_message.content 
                            : `Sent a ${room.last_message.message_type}`
                          }
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : !error ? (
                <div className="text-center py-5">
                  <i className="fas fa-comments fa-3x text-muted mb-3"></i>
                  <h5>No conversations yet</h5>
                  <p className="text-muted">
                    {searchQuery 
                      ? 'No conversations match your search'
                      : 'Your chat conversations will appear here'
                    }
                  </p>
                  {!searchQuery && (
                    <Button 
                      variant="primary" 
                      onClick={() => window.location.href = '/events/'}
                    >
                      <i className="fas fa-calendar me-2"></i>
                      Browse Events
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </Col>

          {/* Chat Main Area */}
          <Col 
            md={8} 
            lg={9} 
            className={`chat-main ${showMobileChat ? 'show' : 'd-none d-md-block'}`}
          >
            {activeChatRoom ? (
              <FullPageChatRoom 
                chatRoom={activeChatRoom}
                onBack={handleBackToList}
              />
            ) : (
              <div className="chat-placeholder">
                <div className="text-center">
                  <i className="fas fa-comments fa-4x text-muted mb-4"></i>
                  <h3>Select a conversation</h3>
                  <p className="text-muted mb-4">
                    Choose a chat from the list to start messaging
                  </p>
                  
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Button 
                      variant="outline-primary"
                      onClick={() => window.location.href = '/events/'}
                    >
                      <i className="fas fa-calendar me-2"></i>
                      Browse Events
                    </Button>
                    <Button 
                      variant="primary"
                      onClick={() => window.location.href = '/'}
                    >
                      <i className="fas fa-home me-2"></i>
                      Go Home
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ChatPage;
